import React, { useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  IconButton,
  Grid,
  Chip,
  Divider,
  Popover,
  TextField,
  Checkbox,
  FormControlLabel,
  Button,
  Stack,
  Tooltip
} from '@mui/material';
import {
  FilterList as FilterIcon,
  Close as CloseIcon,
  Edit as EditIcon,
  Check as CheckIcon,
  Undo as UndoIcon,
  Redo as RedoIcon
} from '@mui/icons-material';
import { MapPin, ListFilter } from 'lucide-react';
import { FilterConfig } from '../types/analytics.types';
import { UnifiedFilterComponent } from './UnifiedFilterComponent';
import { DateRangeFilter } from './DateRangeFilter';
import { NumberRangeFilter } from './NumberRangeFilter';
import { DateConstantsService } from '../services/date-constants.service';

interface GlobalFilterPanelProps {
  filters: FilterConfig[];
  filterValues: Record<string, any>;
  onFilterChange: (newFilterValues: Record<string, any>) => void;
  disabled?: boolean;
  defaultExpanded?: boolean;
  onOpenDialogRef?: (openDialog: () => void) => void; // Ref to expose open dialog function
  // Undo/Redo support
  canUndo?: boolean;
  canRedo?: boolean;
  onUndo?: () => void;
  onRedo?: () => void;
}

export const GlobalFilterPanel: React.FC<GlobalFilterPanelProps> = ({
  filters,
  filterValues,
  onFilterChange,
  disabled = false,
  defaultExpanded = false,
  onOpenDialogRef,
  canUndo = false,
  canRedo = false,
  onUndo,
  onRedo
}) => {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [editingFilterId, setEditingFilterId] = useState<string | null>(null);
  const [filterEditAnchor, setFilterEditAnchor] = useState<HTMLElement | null>(null);
  const [isDateRangeValid, setIsDateRangeValid] = useState(true);

  // Staging logic: track pending changes before they are applied
  const [localFilterValues, setLocalFilterValues] = useState<Record<string, any>>(filterValues);
  const [hasChanges, setHasChanges] = useState(false);
  const [pendingFilterValue, setPendingFilterValue] = useState<any>(null);
  const [filterButtonRef, setFilterButtonRef] = useState<HTMLElement | null>(null);
  const [chipsContainerRef, setChipsContainerRef] = useState<HTMLElement | null>(null);
  const [showOverflowChip, setShowOverflowChip] = useState(false);
  const [visibleChipCount, setVisibleChipCount] = useState(0);

  // Sync local filter values when the prop changes (e.g. from Undo/Redo/Master load)
  // But ONLY if we don't have pending modifications, or if the user explicitly applies
  React.useEffect(() => {
    setLocalFilterValues(filterValues);
    setHasChanges(false);
  }, [filterValues]);

  // Deep comparison helper to check if a single filter has changed
  const isFilterModified = (filterId: string) => {
    const current = filterValues[filterId];
    const pending = localFilterValues[filterId];

    // Simple JSON stringify comparison for arrays/objects
    return JSON.stringify(current) !== JSON.stringify(pending);
  };
  const open = Boolean(anchorEl);

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  // Expose open dialog function via ref - use useRef to prevent infinite loops
  const openDialogRef = React.useRef<(() => void) | null>(null);
  const hasRegisteredRef = React.useRef(false);

  React.useEffect(() => {
    if (filterButtonRef && onOpenDialogRef && !hasRegisteredRef.current) {
      openDialogRef.current = () => {
        setAnchorEl(filterButtonRef);
      };

      // Only call the callback once
      if (openDialogRef.current) {
        onOpenDialogRef(openDialogRef.current);
        hasRegisteredRef.current = true;
      }
    }
  }, [filterButtonRef, onOpenDialogRef]);

  const handleClose = () => {
    // Keep local changes even when closing (user requirement: "If the user closes out of the filter page, the dif is still tehre")
    setPendingFilterValue(null);
    setAnchorEl(null);
    setEditingFilterId(null);
    setFilterEditAnchor(null);
  };

  const handleFilterEditClick = (event: React.MouseEvent<HTMLElement>, filterId: string) => {
    event.stopPropagation();
    setEditingFilterId(filterId);
    setFilterEditAnchor(event.currentTarget);
    // Initialize pending value with local filter value (which might already have pending changes)
    const currentValue = localFilterValues[filterId] || filters.find(f => f.filterId === filterId)?.defaultValue;
    setPendingFilterValue(currentValue);
  };

  const handleFilterEditClose = () => {
    // Discard pending changes when closing or Cancel (without Save)
    setPendingFilterValue(null);
    setEditingFilterId(null);
    setFilterEditAnchor(null);
    setIsDateRangeValid(true); // Reset validation state
  };

  const handleDoneClick = () => {
    // Apply nested changes to our local state
    if (editingFilterId && pendingFilterValue !== null) {
      setLocalFilterValues(prev => {
        const next = { ...prev, [editingFilterId]: pendingFilterValue };
        setHasChanges(JSON.stringify(next) !== JSON.stringify(filterValues));
        return next;
      });
    }
    // Close the popover
    setPendingFilterValue(null);
    setEditingFilterId(null);
    setFilterEditAnchor(null);
  };

  if (!filters || filters.length === 0) {
    return null;
  }

  // Helper to resolve date value (handles date constants)
  const resolveDateValue = (dateValue: any): Date | null => {
    if (!dateValue) return null;
    if (dateValue instanceof Date) return dateValue;
    if (typeof dateValue === 'string') {
      if (DateConstantsService.isDateConstant(dateValue)) {
        const resolvedDateString = DateConstantsService.resolveDateConstant(dateValue);
        return new Date(resolvedDateString);
      }
      return new Date(dateValue);
    }
    return null;
  };

  // Handle filter removal
  const handleFilterRemove = (filterId: string) => {
    const filter = filters.find(f => f.filterId === filterId);
    if (!filter) return;

    // Reset filter locally
    const resetValue = filter.defaultValue !== undefined ? filter.defaultValue : null;
    setLocalFilterValues(prev => {
      const next = { ...prev, [filterId]: resetValue };
      setHasChanges(JSON.stringify(next) !== JSON.stringify(filterValues));
      return next;
    });
  };

  // Handle removal of individual option from multi-select filter
  const handleOptionRemove = (filterId: string, optionValue: string) => {
    return (event: React.MouseEvent) => {
      event.stopPropagation();
      event.preventDefault();

      const currentValue = filterValues[filterId];
      if (!Array.isArray(currentValue)) return;

      const newValues = currentValue.filter(v => v !== optionValue);

      // If all chips are removed, reset to select all options for Order Types or Payment Channels
      if (newValues.length === 0) {
        const filter = filters.find(f => f.filterId === filterId);
        if (filter && filter.options) {
          // Check if this is Order Types or Payment Channels filter
          const isOrderTypesFilter = filter.label?.toLowerCase().includes('order type') ||
            filter.filterId?.toLowerCase().includes('order-type') ||
            filter.filterId?.toLowerCase().includes('order_type');
          const isPaymentChannelsFilter = filter.label?.toLowerCase().includes('payment channel') ||
            filter.filterId?.toLowerCase().includes('payment-channel') ||
            filter.filterId?.toLowerCase().includes('payment_channel');

          if (isOrderTypesFilter || isPaymentChannelsFilter) {
            // Select all options
            const allValues = filter.options.map(opt => opt.value);
            onFilterChange({ [filterId]: allValues });
            return;
          }
        }
      }

      setLocalFilterValues(prev => {
        const next = { ...prev, [filterId]: newValues };
        setHasChanges(JSON.stringify(next) !== JSON.stringify(filterValues));
        return next;
      });
    };
  };

  // Format chip label with title case and special overrides
  const formatChipLabel = (label: string, value?: string): string => {
    // Special overrides based on value
    if (value === 'cake_pos') {
      return 'CAKE POS';
    }
    if (value === 'olo_third_party') {
      return 'OLO 3rd Party';
    }
    if (value === 'dinein' || value === 'dine_in' || label.toLowerCase() === 'dinein') {
      return 'Dine In';
    }

    // Title case: capitalize first letter of each word
    return label
      .split(' ')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ');
  };

  // Custom X icon component (no circle) - exact match from Figma
  const CustomDeleteIcon = () => (
    <CloseIcon
      sx={{
        width: '16px !important',
        height: '16px !important',
        flex: 'none !important',
        order: 2,
        flexGrow: 0,
        color: '#F4F4F5 !important',
        fontSize: '16px !important',
        margin: '0 !important',
        padding: '0 !important',
        backgroundColor: 'transparent !important',
        borderRadius: '0 !important',
        position: 'relative',
        zIndex: 10001, // Higher than chip z-index
        pointerEvents: 'auto',
        cursor: 'pointer',
        '&:hover': {
          color: '#F4F4F5 !important',
          backgroundColor: 'transparent !important',
        },
        '& path': {
          stroke: 'none !important',
          strokeWidth: '0 !important',
          fill: '#F4F4F5 !important',
        },
      }}
    />
  );

  // Generate filter summary for collapsed view
  const getFilterSummary = (): React.ReactNode[] => {
    const summaryElements: React.ReactNode[] = [];
    let locationFilter: FilterConfig | null = null;

    // First, find and process the location filter separately
    filters.forEach((filter) => {
      const isLocationFilter = filter.label?.toLowerCase().includes('location') ||
        filter.filterId?.toLowerCase().includes('location');

      if (isLocationFilter) {
        locationFilter = filter;
      }
    });

    // Always show location chip - find or create default location filter
    if (!locationFilter) {
      // Try to find it by checking all filters more broadly
      locationFilter = filters.find(f =>
        f.label?.toLowerCase().includes('location') ||
        f.filterId?.toLowerCase().includes('location') ||
        f.filterId?.toLowerCase().includes('loc')
      ) || null;
    }

    // Always show location chip - use found filter or create a default display
    const locationValue = locationFilter ? filterValues[locationFilter.filterId] : undefined;
    const locationCount = Array.isArray(locationValue) ? locationValue.length : (locationValue ? 1 : 0);
    const locationLabel = locationFilter?.label || 'Locations';

    // Always show location chip, even if no filter exists
    const locationChipSx = {
      display: 'flex !important',
      flexDirection: 'row !important',
      justifyContent: 'center !important',
      alignItems: 'center !important',
      padding: '8px 12px !important',
      gap: '8px !important',
      height: '32px !important',
      backgroundColor: '#18181B !important',
      boxShadow: '0px 1px 3px rgba(0, 0, 0, 0.1), 0px 1px 2px rgba(0, 0, 0, 0.06) !important',
      borderRadius: '9999px !important',
      fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Oxygen', 'Ubuntu', 'Cantarell', 'Fira Sans', 'Droid Sans', 'Helvetica Neue', sans-serif !important",
      fontStyle: 'normal !important',
      fontWeight: '500 !important',
      fontSize: '12px !important',
      lineHeight: '16px !important',
      color: '#FAFAFA !important',
      textTransform: 'none !important',
      letterSpacing: 'normal !important',
      '& .MuiChip-label': {
        padding: '0 !important',
        fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Oxygen', 'Ubuntu', 'Cantarell', 'Fira Sans', 'Droid Sans', 'Helvetica Neue', sans-serif !important",
        fontStyle: 'normal !important',
        fontWeight: '500 !important',
        fontSize: '12px !important',
        lineHeight: '16px !important',
        display: 'flex !important',
        alignItems: 'center !important',
        color: '#FAFAFA !important',
        textTransform: 'none !important',
        letterSpacing: 'normal !important',
      },
      '& .MuiChip-deleteIcon': {
        display: 'none !important',
      },
    };

    summaryElements.push(
      <Chip
        key={locationFilter?.filterId || 'location-default'}
        label={
          <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <MapPin size={16} color="#F4F4F5" style={{ flexShrink: 0 }} />
            <span>
              {locationCount === 0 ? 'All Locations' : `${locationCount} ${locationLabel}`}
              {locationFilter && isFilterModified(locationFilter.filterId) && (
                <span style={{ color: '#f97316', marginLeft: '4px' }}>*</span>
              )}
            </span>
          </Box>
        }
        {...(locationCount > 0 && {
          onDelete: (e: React.MouseEvent) => {
            e.stopPropagation();
            e.preventDefault();
            if (locationFilter) {
              handleFilterRemove(locationFilter.filterId);
            }
          },
          deleteIcon: <CustomDeleteIcon />,
          onClick: (e: React.MouseEvent) => {
            // Only open filter menu if clicking the label, not the delete icon
            if ((e.target as HTMLElement).closest('.MuiChip-deleteIcon')) {
              e.stopPropagation();
              e.preventDefault();
              return;
            }
          },
        })}
        sx={{
          ...locationChipSx,
          ...(locationCount > 0 && {
            position: 'relative',
            '& .MuiChip-deleteIcon': {
              display: 'block !important', // Show delete icon only when there's a number
              position: 'relative',
              zIndex: 10001, // Higher than chip
              pointerEvents: 'auto',
              cursor: 'pointer',
            },
            '& .MuiChip-label': {
              cursor: 'pointer', // Show pointer cursor on label
              position: 'relative',
              zIndex: 1,
            },
          }),
        }}
      />
    );


    // Process all other filters
    filters.forEach((filter) => {
      // Skip location filter as it's already processed
      const isLocationFilter = filter.label?.toLowerCase().includes('location') ||
        filter.filterId?.toLowerCase().includes('location');
      if (isLocationFilter) return;

      const value = filterValues[filter.filterId];

      if (value === undefined || value === null) return;

      // Common chip styling from Figma - exact match with aggressive overrides
      const chipSx = {
        display: 'flex !important',
        flexDirection: 'row !important',
        justifyContent: 'center !important',
        alignItems: 'center !important',
        padding: '8px 12px !important',
        gap: '8px !important',
        height: '32px !important',
        backgroundColor: '#18181B !important',
        boxShadow: '0px 1px 3px rgba(0, 0, 0, 0.1), 0px 1px 2px rgba(0, 0, 0, 0.06) !important',
        borderRadius: '9999px !important',
        fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Oxygen', 'Ubuntu', 'Cantarell', 'Fira Sans', 'Droid Sans', 'Helvetica Neue', sans-serif !important",
        fontStyle: 'normal !important',
        fontWeight: '500 !important',
        fontSize: '12px !important',
        lineHeight: '16px !important',
        color: '#FAFAFA !important',
        textTransform: 'none !important',
        letterSpacing: 'normal !important',
        '&.MuiChip-root': {
          fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Oxygen', 'Ubuntu', 'Cantarell', 'Fira Sans', 'Droid Sans', 'Helvetica Neue', sans-serif !important",
          fontStyle: 'normal !important',
          fontWeight: '500 !important',
          fontSize: '12px !important',
          lineHeight: '16px !important',
          color: '#FAFAFA !important',
        },
        '& .MuiChip-label': {
          padding: '0 !important',
          fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Oxygen', 'Ubuntu', 'Cantarell', 'Fira Sans', 'Droid Sans', 'Helvetica Neue', sans-serif !important",
          fontStyle: 'normal !important',
          fontWeight: '500 !important',
          fontSize: '12px !important',
          lineHeight: '16px !important',
          display: 'flex !important',
          alignItems: 'center !important',
          color: '#FAFAFA !important',
          textTransform: 'none !important',
          letterSpacing: 'normal !important',
        },
        '& *': {
          fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Oxygen', 'Ubuntu', 'Cantarell', 'Fira Sans', 'Droid Sans', 'Helvetica Neue', sans-serif !important",
        },
        '& .MuiChip-deleteIcon': {
          display: 'none !important', // Hide default delete icon
        },
        '& .MuiChip-deleteIcon:hover': {
          backgroundColor: 'transparent !important',
        },
        '& .MuiChip-deleteIcon::before': {
          display: 'none !important',
        },
        '& .MuiChip-deleteIcon::after': {
          display: 'none !important',
        },
      };

      // Handle different filter types
      switch (filter.type) {
        case 'date-range':
          if (value?.start || value?.end) {
            const startDate = resolveDateValue(value.start);
            const endDate = resolveDateValue(value.end);

            // Format date as "Aug 15" or "Aug 15 2025" using UTC to match date picker
            const formatDate = (date: Date | null, includeYear: boolean = false): string => {
              if (!date) return '...';
              const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
              const month = months[date.getUTCMonth()];
              const day = date.getUTCDate();
              const year = date.getUTCFullYear();
              return includeYear ? `${month} ${day} ${year}` : `${month} ${day}`;
            };

            // Check if years match using UTC
            const startYear = startDate?.getUTCFullYear();
            const endYear = endDate?.getUTCFullYear();
            const yearsMatch = startYear === endYear;

            const startStr = formatDate(startDate, !yearsMatch); // Include year only if years don't match
            const endStr = formatDate(endDate, true); // Always include year on end date

            // Only show the range, no title - never show delete icon
            summaryElements.push(
              <Chip
                key={filter.filterId}
                label={
                  <span>
                    {`${startStr} – ${endStr}`}
                    {isFilterModified(filter.filterId) && (
                      <span style={{ color: '#f97316', marginLeft: '4px' }}>*</span>
                    )}
                  </span>
                }
                sx={{
                  ...chipSx,
                  '& .MuiChip-deleteIcon': {
                    display: 'none !important', // Never show delete icon for date range chips
                  },
                }}
              />
            );
          }
          break;

        case 'multi-select':
          if (Array.isArray(value) && value.length > 0) {
            // Check if this is Order types or Payment Channels filter
            const isOrderTypesFilter = filter.label?.toLowerCase().includes('order type') ||
              filter.filterId?.toLowerCase().includes('order-type') ||
              filter.filterId?.toLowerCase().includes('order_type');
            const isPaymentChannelsFilter = filter.label?.toLowerCase().includes('payment channel') ||
              filter.filterId?.toLowerCase().includes('payment-channel') ||
              filter.filterId?.toLowerCase().includes('payment_channel');
            const isChipFilter = isOrderTypesFilter || isPaymentChannelsFilter;
            const totalOptions = filter.options?.length || 0;
            const selectedCount = value.length;
            const isMaximum = isChipFilter && totalOptions > 0 && selectedCount === totalOptions;

            if (isMaximum) {
              break; // Skip displaying the chip when all options are selected
            }

            // For Order Types and Payment Channels, show individual chips for each selected option
            if (isChipFilter && filter.options) {
              value.forEach((optionValue: string) => {
                const option = filter.options?.find(opt => opt.value === optionValue);
                if (option) {
                  summaryElements.push(
                    <Chip
                      key={`${filter.filterId}-${optionValue}`}
                      label={
                        <span>
                          {formatChipLabel(option.label, optionValue)}
                          {isFilterModified(filter.filterId) && (
                            <span style={{ color: '#f97316', marginLeft: '4px' }}>*</span>
                          )}
                        </span>
                      }
                      onDelete={(e) => {
                        e.stopPropagation();
                        e.preventDefault();
                        const currentValue = filterValues[filter.filterId];
                        if (!Array.isArray(currentValue)) return;

                        const newValues = currentValue.filter(v => v !== optionValue);

                        // If all chips are removed, reset to select all options for Order Types or Payment Channels
                        if (newValues.length === 0) {
                          const currentFilter = filters.find(f => f.filterId === filter.filterId);
                          if (currentFilter && currentFilter.options) {
                            const isOrderTypesFilter = currentFilter.label?.toLowerCase().includes('order type') ||
                              currentFilter.filterId?.toLowerCase().includes('order-type') ||
                              currentFilter.filterId?.toLowerCase().includes('order_type');
                            const isPaymentChannelsFilter = currentFilter.label?.toLowerCase().includes('payment channel') ||
                              currentFilter.filterId?.toLowerCase().includes('payment-channel') ||
                              currentFilter.filterId?.toLowerCase().includes('payment_channel');

                            if (isOrderTypesFilter || isPaymentChannelsFilter) {
                              const allValues = currentFilter.options.map(opt => opt.value);
                              setLocalFilterValues(prev => {
                                const next = { ...prev, [filter.filterId]: allValues };
                                setHasChanges(JSON.stringify(next) !== JSON.stringify(filterValues));
                                return next;
                              });
                              return;
                            }
                          }
                        }

                        setLocalFilterValues(prev => {
                          const next = { ...prev, [filter.filterId]: newValues };
                          setHasChanges(JSON.stringify(next) !== JSON.stringify(filterValues));
                          return next;
                        });
                      }}
                      deleteIcon={<CustomDeleteIcon />}
                      onClick={(e) => {
                        // Only open filter menu if clicking the label, not the delete icon
                        if ((e.target as HTMLElement).closest('.MuiChip-deleteIcon')) {
                          e.stopPropagation();
                          e.preventDefault();
                          // Trigger the delete functionality
                          const currentValue = filterValues[filter.filterId];
                          if (!Array.isArray(currentValue)) return;

                          const newValues = currentValue.filter(v => v !== optionValue);

                          // If all chips are removed, reset to select all options
                          if (newValues.length === 0) {
                            const currentFilter = filters.find(f => f.filterId === filter.filterId);
                            if (currentFilter && currentFilter.options) {
                              const isOrderTypesFilter = currentFilter.label?.toLowerCase().includes('order type') ||
                                currentFilter.filterId?.toLowerCase().includes('order-type') ||
                                currentFilter.filterId?.toLowerCase().includes('order_type');
                              const isPaymentChannelsFilter = currentFilter.label?.toLowerCase().includes('payment channel') ||
                                currentFilter.filterId?.toLowerCase().includes('payment-channel') ||
                                currentFilter.filterId?.toLowerCase().includes('payment_channel');

                              if (isOrderTypesFilter || isPaymentChannelsFilter) {
                                const allValues = currentFilter.options.map(opt => opt.value);
                                setLocalFilterValues(prev => {
                                  const next = { ...prev, [filter.filterId]: allValues };
                                  setHasChanges(JSON.stringify(next) !== JSON.stringify(filterValues));
                                  return next;
                                });
                                return;
                              }
                            }
                          }

                          setLocalFilterValues(prev => {
                            const next = { ...prev, [filter.filterId]: newValues };
                            setHasChanges(JSON.stringify(next) !== JSON.stringify(filterValues));
                            return next;
                          });
                          return;
                        }
                        // Otherwise, let the click bubble up to open the filter menu
                      }}
                      sx={{
                        ...chipSx,
                        position: 'relative',
                        '& .MuiChip-deleteIcon': {
                          display: 'block !important', // Show delete icon for individual option chips
                          position: 'relative',
                          zIndex: 10001, // Higher than chip
                          pointerEvents: 'auto',
                          cursor: 'pointer',
                        },
                        '& .MuiChip-label': {
                          cursor: 'pointer', // Show pointer cursor on label
                          position: 'relative',
                          zIndex: 1,
                        },
                      }}
                    />
                  );
                }
              });
            } else {
              // For other multi-select filters, show count format: "97 Locations", "5 Order Types", etc.
              const count = value.length;
              const label = formatChipLabel(filter.label || 'Items');
              summaryElements.push(
                <Chip
                  key={filter.filterId}
                  label={
                    <span>
                      {`${count} ${label}`}
                      {isFilterModified(filter.filterId) && (
                        <span style={{ color: '#f97316', marginLeft: '4px' }}>*</span>
                      )}
                    </span>
                  }
                  onDelete={(e) => {
                    e.stopPropagation();
                    e.preventDefault();
                    handleFilterRemove(filter.filterId);
                  }}
                  deleteIcon={<CustomDeleteIcon />}
                  onClick={(e) => {
                    // Only open filter menu if clicking the label, not the delete icon
                    if ((e.target as HTMLElement).closest('.MuiChip-deleteIcon')) {
                      e.stopPropagation();
                      e.preventDefault();
                      return;
                    }
                  }}
                  sx={{
                    ...chipSx,
                    position: 'relative',
                    '& .MuiChip-deleteIcon': {
                      display: 'block !important', // Show delete icon
                      position: 'relative',
                      zIndex: 10001, // Higher than chip
                      pointerEvents: 'auto',
                      cursor: 'pointer',
                    },
                    '& .MuiChip-label': {
                      cursor: 'pointer', // Show pointer cursor on label
                      position: 'relative',
                      zIndex: 1,
                    },
                  }}
                />
              );
            }
          }
          break;

        case 'select':
          if (value) {
            const label = formatChipLabel(filter.label || 'Item', value);
            summaryElements.push(
              <Chip
                key={filter.filterId}
                label={`1 ${label}`}
                onDelete={(e) => {
                  e.stopPropagation();
                  e.preventDefault();
                  handleFilterRemove(filter.filterId);
                }}
                deleteIcon={<CustomDeleteIcon />}
                onClick={(e) => {
                  // Only open filter menu if clicking the label, not the delete icon
                  if ((e.target as HTMLElement).closest('.MuiChip-deleteIcon')) {
                    e.stopPropagation();
                    e.preventDefault();
                    return;
                  }
                }}
                sx={{
                  ...chipSx,
                  position: 'relative',
                  '& .MuiChip-deleteIcon': {
                    display: 'block !important', // Show delete icon
                    position: 'relative',
                    zIndex: 10001, // Higher than chip
                    pointerEvents: 'auto',
                    cursor: 'pointer',
                  },
                  '& .MuiChip-label': {
                    cursor: 'pointer', // Show pointer cursor on label
                    position: 'relative',
                    zIndex: 1,
                  },
                }}
              />
            );
          }
          break;

        case 'number-range':
          // Skip number-range filters in summary chips
          break;

        case 'checkbox':
          if (value === true) {
            const label = formatChipLabel(filter.label || 'Item');
            summaryElements.push(
              <Chip
                key={filter.filterId}
                label={label}
                onDelete={(e) => {
                  e.stopPropagation();
                  e.preventDefault();
                  handleFilterRemove(filter.filterId);
                }}
                deleteIcon={<CustomDeleteIcon />}
                onClick={(e) => {
                  // Only open filter menu if clicking the label, not the delete icon
                  if ((e.target as HTMLElement).closest('.MuiChip-deleteIcon')) {
                    e.stopPropagation();
                    e.preventDefault();
                    return;
                  }
                }}
                sx={{
                  ...chipSx,
                  position: 'relative',
                  '& .MuiChip-deleteIcon': {
                    display: 'block !important', // Show delete icon
                    position: 'relative',
                    zIndex: 10001, // Higher than chip
                    pointerEvents: 'auto',
                    cursor: 'pointer',
                  },
                  '& .MuiChip-label': {
                    cursor: 'pointer', // Show pointer cursor on label
                    position: 'relative',
                    zIndex: 1,
                  },
                }}
              />
            );
          }
          break;

        case 'search':
          // Skip search filters in summary chips
          break;
      }
    });

    return summaryElements;
  };

  const filterSummary = getFilterSummary();
  const hasActiveFilters = filterSummary.length > 0;
  const activeFilterCount = filterSummary.length;

  // Detect overflow and calculate visible chip count
  React.useEffect(() => {
    if (!chipsContainerRef || !hasActiveFilters) {
      setShowOverflowChip(false);
      setVisibleChipCount(filterSummary.length);
      return;
    }

    const checkOverflow = () => {
      const container = chipsContainerRef;
      if (!container) return;

      // Get the parent container to know available width
      const parentContainer = container.parentElement;
      if (!parentContainer) return;

      const containerWidth = parentContainer.offsetWidth;
      const filterButton = parentContainer.querySelector('button');
      const buttonWidth = filterButton ? filterButton.offsetWidth : 0;
      const gap = 24; // 1.5 * 16px = 24px gap between chips container and button

      // Available width for chips = total width - button width - gap
      const availableWidth = containerWidth - buttonWidth - gap;

      const children = Array.from(container.children) as HTMLElement[];

      // Filter out the overflow chip if it exists
      const filterChips = children.filter(child => !child.textContent?.startsWith('+'));

      if (filterChips.length === 0) {
        setShowOverflowChip(false);
        setVisibleChipCount(0);
        return;
      }

      let totalWidth = 0;
      let visibleCount = 0;

      // Account for gap between chips (0.5 * 8px = 4px)
      const chipGap = 4;

      // Estimate overflow chip width (approximate: "+X" text + padding)
      const overflowChipWidth = 60; // Approximate width for overflow chip

      for (let i = 0; i < filterChips.length; i++) {
        const child = filterChips[i];
        const childWidth = child.offsetWidth;

        // Add gap for all but the first chip
        const widthWithGap = totalWidth > 0 ? childWidth + chipGap : childWidth;

        // Check if we need space for overflow chip
        const needsOverflowChip = i < filterChips.length - 1;
        const requiredWidth = needsOverflowChip ? widthWithGap + chipGap + overflowChipWidth : widthWithGap;

        if (totalWidth + requiredWidth <= availableWidth || !needsOverflowChip) {
          totalWidth += widthWithGap;
          visibleCount++;
        } else {
          break;
        }
      }

      setVisibleChipCount(visibleCount);
      setShowOverflowChip(visibleCount < filterChips.length);
    };

    // Initial check
    checkOverflow();

    // Use ResizeObserver to detect container size changes
    const resizeObserver = new ResizeObserver(checkOverflow);
    resizeObserver.observe(chipsContainerRef);
    if (chipsContainerRef.parentElement) {
      resizeObserver.observe(chipsContainerRef.parentElement);
    }

    // Also check on window resize
    window.addEventListener('resize', checkOverflow);

    return () => {
      resizeObserver.disconnect();
      window.removeEventListener('resize', checkOverflow);
    };
  }, [chipsContainerRef, filterSummary.length, hasActiveFilters]);

  // Get compact display value for a filter
  const getFilterDisplayValue = (filter: FilterConfig, values: Record<string, any> = filterValues): string => {
    const value = values[filter.filterId];

    if (!value) return 'Not set';

    switch (filter.type) {
      case 'date-range':
        if (value?.start || value?.end) {
          const startDate = resolveDateValue(value.start);
          const endDate = resolveDateValue(value.end);
          // Format dates using UTC to match date picker
          const formatUTCDate = (d: Date | null) => {
            if (!d) return '...';
            return `${d.getUTCMonth() + 1}/${d.getUTCDate()}/${d.getUTCFullYear()}`;
          };
          const startStr = formatUTCDate(startDate);
          const endStr = formatUTCDate(endDate);
          return `${startStr} - ${endStr}`;
        }
        return 'Not set';

      case 'multi-select':
        if (Array.isArray(value) && value.length > 0) {
          return value.length > 2 ? `${value.length} selected` : value.join(', ');
        }
        return 'None selected';

      case 'select':
        if (value) {
          const option = filter.options?.find(opt => opt.value === value);
          return option?.label || value;
        }
        return 'Not selected';

      case 'number-range':
        if (value?.min !== undefined || value?.max !== undefined) {
          const minStr = value.min !== null ? value.min : '...';
          const maxStr = value.max !== null ? value.max : '...';
          return `${minStr} - ${maxStr}`;
        }
        return 'Not set';

      case 'checkbox':
        return value === true ? 'Yes' : 'No';

      case 'search':
        return value && value.trim() !== '' ? `"${value}"` : 'Not set';

      default:
        return 'Not set';
    }
  };

  // Render compact filter row
  const renderCompactFilter = (filter: FilterConfig) => {
    const displayValue = getFilterDisplayValue(filter, localFilterValues);
    const isSet = displayValue !== 'Not set' && displayValue !== 'None selected' && displayValue !== 'Not selected';

    // Check if this is Order Types or Payment Channels filter
    const isOrderTypesFilter = filter.label?.toLowerCase().includes('order type') ||
      filter.filterId?.toLowerCase().includes('order-type') ||
      filter.filterId?.toLowerCase().includes('order_type');
    const isPaymentChannelsFilter = filter.label?.toLowerCase().includes('payment channel') ||
      filter.filterId?.toLowerCase().includes('payment-channel') ||
      filter.filterId?.toLowerCase().includes('payment_channel');
    const isLocationFilter = filter.label?.toLowerCase().includes('location') ||
      filter.filterId?.toLowerCase().includes('location');
    const isChipFilter = isOrderTypesFilter || isPaymentChannelsFilter;

    const filterValue = localFilterValues[filter.filterId];
    const selectedValues = Array.isArray(filterValue) ? filterValue : [];
    const locationCount = isLocationFilter ? selectedValues.length : 0;
    const isModified = isFilterModified(filter.filterId);

    return (
      <Paper
        key={filter.filterId}
        onClick={(e) => handleFilterEditClick(e, filter.filterId)}
        sx={{
          p: 2,
          bgcolor: '#ffffff',
          borderRadius: '8px',
          border: '1px solid',
          borderColor: isSet ? '#18181b' : '#e4e4e7',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          cursor: 'pointer',
          transition: 'all 0.2s',
          boxShadow: '0 1px 2px 0 rgba(0, 0, 0, 0.05)',
          '&:hover': {
            borderColor: '#18181b',
            bgcolor: '#fafafa',
            boxShadow: '0 2px 4px 0 rgba(0, 0, 0, 0.1)'
          }
        }}
      >
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography
            variant="caption"
            sx={{
              color: '#71717a',
              display: 'block',
              mb: 0.5,
              fontFamily: "'Inter', sans-serif",
              fontWeight: 500,
              fontSize: '12px',
              lineHeight: '16px'
            }}
          >
            {filter.label}
            {isModified && (
              <span style={{ color: '#f97316', marginLeft: '4px' }}>*</span>
            )}
          </Typography>
          {isChipFilter && filter.options ? (
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 0.5 }}>
              {filter.options.map((option) => {
                const isSelected = selectedValues.includes(option.value);
                return (
                  <Chip
                    key={option.value}
                    label={formatChipLabel(option.label, option.value)}
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation();
                      const newValues = isSelected
                        ? selectedValues.filter(v => v !== option.value)
                        : [...selectedValues, option.value];
                      setLocalFilterValues(prev => {
                        const next = { ...prev, [filter.filterId]: newValues };
                        setHasChanges(JSON.stringify(next) !== JSON.stringify(filterValues));
                        return next;
                      });
                    }}
                    sx={{
                      height: '16px',
                      fontFamily: "'Inter', sans-serif",
                      fontSize: '10px',
                      lineHeight: '12px',
                      fontWeight: 500,
                      cursor: 'pointer',
                      minWidth: 'auto',
                      ...(isSelected ? {
                        backgroundColor: '#f97316',
                        color: '#ffffff',
                        border: '1px solid #f97316',
                      } : {
                        backgroundColor: 'transparent',
                        color: '#18181b',
                        border: '1px solid #e4e4e7',
                      }),
                      borderRadius: '3px',
                      padding: '0 4px',
                      '&:hover': {
                        ...(isSelected ? {
                          backgroundColor: '#ea580c',
                        } : {
                          backgroundColor: '#fff7ed',
                          borderColor: '#f97316',
                        }),
                      },
                      '& .MuiChip-label': {
                        padding: '0 2px',
                        height: '16px',
                        display: 'flex',
                        alignItems: 'center',
                      },
                    }}
                  />
                );
              })}
            </Box>
          ) : isLocationFilter ? (
            <Chip
              label={locationCount === 0 ? 'All Locations' : `${locationCount} Selected`}
              size="small"
              sx={{
                height: '24px',
                fontFamily: "'Inter', sans-serif",
                fontSize: '12px',
                lineHeight: '16px',
                fontWeight: 500,
                backgroundColor: '#f97316',
                color: '#ffffff',
                border: '1px solid #f97316',
                borderRadius: '9999px',
                padding: '0 12px',
                '&:hover': {
                  backgroundColor: '#ea580c',
                },
                '& .MuiChip-label': {
                  padding: '0',
                  height: '24px',
                  display: 'flex',
                  alignItems: 'center',
                },
              }}
            />
          ) : (
            <Typography
              variant="body2"
              sx={{
                color: isSet ? '#18181b' : '#71717a',
                fontFamily: "'Inter', sans-serif",
                fontWeight: isSet ? 500 : 400,
                fontSize: '14px',
                lineHeight: '20px',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap'
              }}
            >
              {displayValue}
            </Typography>
          )}
        </Box>
        {isChipFilter && filter.options ? (
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', ml: 2 }}>
            {(() => {
              // Check if all options are selected
              const allSelected = selectedValues.length === filter.options.length;

              // Check if default value contains all options (all chips defaulted to selected)
              const defaultValue = filter.defaultValue;
              const allOptionsDefaulted = Array.isArray(defaultValue) &&
                defaultValue.length === filter.options.length &&
                filter.options.every(opt => defaultValue.includes(opt.value));

              // If all are selected (whether by default or manually), show "Deselect All"
              // But if all were defaulted to selected, always show "Deselect All" when all are selected
              if (allSelected) {
                return (
                  <Typography
                    onClick={(e) => {
                      e.stopPropagation();
                      setLocalFilterValues(prev => {
                        const next = { ...prev, [filter.filterId]: [] };
                        setHasChanges(JSON.stringify(next) !== JSON.stringify(filterValues));
                        return next;
                      });
                    }}
                    sx={{
                      fontFamily: "'Inter', sans-serif",
                      fontSize: '12px',
                      lineHeight: '16px',
                      fontWeight: 400,
                      color: '#71717a',
                      cursor: 'pointer',
                      textDecoration: 'underline',
                      '&:hover': {
                        color: '#18181b',
                      },
                    }}
                  >
                    Deselect All
                  </Typography>
                );
              }

              // Otherwise show "Select All"
              return (
                <Typography
                  onClick={(e) => {
                    e.stopPropagation();
                    const allValues = filter.options.map(opt => opt.value);
                    setLocalFilterValues(prev => {
                      const next = { ...prev, [filter.filterId]: allValues };
                      setHasChanges(JSON.stringify(next) !== JSON.stringify(filterValues));
                      return next;
                    });
                  }}
                  sx={{
                    fontFamily: "'Inter', sans-serif",
                    fontSize: '12px',
                    lineHeight: '16px',
                    fontWeight: 400,
                    color: '#71717a',
                    cursor: 'pointer',
                    textDecoration: 'underline',
                    '&:hover': {
                      color: '#18181b',
                    },
                  }}
                >
                  Select All
                </Typography>
              );
            })()}
          </Box>
        ) : (
          <IconButton
            size="small"
            sx={{
              ml: 2,
              color: '#71717a',
              '&:hover': {
                color: '#18181b',
                bgcolor: '#f4f4f5'
              }
            }}
            onClick={(e) => {
              e.stopPropagation();
              handleFilterEditClick(e, filter.filterId);
            }}
          >
            <EditIcon fontSize="small" />
          </IconButton>
        )}
      </Paper>
    );
  };

  // Render individual filter based on type (for editing popover)
  const renderFilter = (filter: FilterConfig) => {
    // Use pending value if editing this filter, otherwise use current value
    const displayValue = editingFilterId === filter.filterId && pendingFilterValue !== null
      ? pendingFilterValue
      : (localFilterValues[filter.filterId] || filter.defaultValue);

    switch (filter.type) {
      case 'date-range':
        const resolveDate = (dateValue: any): Date | null => {
          if (!dateValue) return null;
          if (dateValue instanceof Date) return dateValue;
          if (typeof dateValue === 'string') {
            if (DateConstantsService.isDateConstant(dateValue)) {
              const resolvedDateString = DateConstantsService.resolveDateConstant(dateValue);
              return new Date(resolvedDateString);
            }
            return new Date(dateValue);
          }
          return null;
        };

        const startDate = resolveDate(displayValue?.start);
        const endDate = resolveDate(displayValue?.end);
        const startTime = displayValue?.startTime;
        const endTime = displayValue?.endTime;

        return (
          <DateRangeFilter
            startDate={startDate}
            endDate={endDate}
            startTime={startTime}
            endTime={endTime}
            onChange={(start, end, startTime, endTime) => {
              // Store in pending state instead of immediately applying
              const value: any = { start, end };
              if (startTime !== undefined) value.startTime = startTime;
              if (endTime !== undefined) value.endTime = endTime;
              setPendingFilterValue(value);
            }}
            onValidationChange={(isValid) => {
              setIsDateRangeValid(isValid);
            }}
            disabled={disabled}
            label={filter.label}
            showQuickButtons={true}
          // Business Day Configuration is now in Settings tab, not in filters
          // Remove customReportingTime prop to hide Business Day Configuration UI
          />
        );

      case 'number-range':
        return (
          <NumberRangeFilter
            min={displayValue?.min}
            max={displayValue?.max}
            onChange={(min, max) => {
              // Store in pending state instead of immediately applying
              setPendingFilterValue({ min, max });
            }}
            disabled={disabled}
            label={filter.label}
            placeholder={filter.placeholder}
            clearable={filter.clearable !== false}
          />
        );

      case 'multi-select':
      case 'select':
        return (
          <UnifiedFilterComponent
            title={filter.label}
            options={filter.options || []}
            value={Array.isArray(displayValue) ? displayValue : [displayValue].filter(Boolean)}
            placeholder={filter.placeholder}
            onChange={(newValues) => {
              // Store in pending state instead of immediately applying
              if (filter.type === 'select') {
                setPendingFilterValue(newValues[0] || null);
              } else {
                setPendingFilterValue(newValues);
              }
            }}
            disabled={disabled}
            allowSelectAll={filter.allowSelectAll !== false}
            hierarchical={filter.hierarchical || false}
            defaultExpanded={filter.defaultExpanded || false}
          />
        );

      default:
        return (
          <Paper sx={{ p: 2, bgcolor: '#ffffff', borderRadius: '8px', border: '1px solid #e4e4e7' }}>
            <Typography
              variant="body2"
              sx={{
                color: '#71717a',
                fontFamily: "'Inter', sans-serif",
                fontSize: '14px',
                lineHeight: '20px'
              }}
            >
              Filter type "{filter.type}" not yet supported in global filters
            </Typography>
          </Paper>
        );
    }
  };

  return (
    <>
      {/* Inline Filter Button with Summary Chips - Entire area is clickable */}
      <Box
        ref={(el) => setFilterButtonRef(el as HTMLElement)}
        onClick={handleClick}
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
          cursor: 'pointer',
          width: '100%',
          minWidth: 0,
          justifyContent: 'flex-start',
          position: 'relative',
          zIndex: 9999, // Move to very front Z position
          '&:hover .filter-chip': {
            backgroundColor: activeFilterCount > 0 ? 'primary.dark' : 'grey.800'
          }
        }}
      >
        {/* Active Filter Summary Chips */}
        <Box
          ref={setChipsContainerRef}
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 0.5,
            flexWrap: 'nowrap',
            overflow: 'hidden',
            minWidth: 0,
            pointerEvents: 'auto', // Allow chips to be clickable
            position: 'relative',
            zIndex: 10000 // Ensure chips are on top
          }}
        >
          {hasActiveFilters && (
            <>
              {filterSummary.slice(0, showOverflowChip ? visibleChipCount : filterSummary.length)}
              {showOverflowChip && (
                <Chip
                  label={`+${filterSummary.length - visibleChipCount}`}
                  sx={{
                    backgroundColor: '#18181b !important',
                    color: '#fafafa !important',
                    fontFamily: "'Inter', sans-serif !important",
                    fontWeight: 500,
                    fontSize: '12px !important',
                    lineHeight: '16px !important',
                    height: 'auto !important',
                    minHeight: 'auto !important',
                    maxHeight: 'none !important',
                    padding: '8px 12px !important',
                    borderRadius: '9999px !important',
                    boxShadow: '0px 1px 3px 0px rgba(0,0,0,0.1), 0px 1px 2px 0px rgba(0,0,0,0.06) !important',
                    minWidth: 'auto',
                    flexShrink: 0,
                    '& .MuiChip-label': {
                      padding: '0 !important',
                      fontFamily: "'Inter', sans-serif !important",
                      fontWeight: 500,
                      fontSize: '12px !important',
                      lineHeight: '16px !important',
                    },
                  }}
                />
              )}
            </>
          )}
        </Box>

        {/* Filter Button - matches Figma design */}
        <Button
          onClick={handleClick}
          disabled={disabled}
          sx={{
            flexShrink: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '8px',
            height: '36px',
            padding: '8px 12px',
            backgroundColor: '#ffffff',
            border: '1px solid #e4e4e7',
            borderRadius: '6px',
            fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Oxygen', 'Ubuntu', 'Cantarell', 'Fira Sans', 'Droid Sans', 'Helvetica Neue', sans-serif",
            fontStyle: 'normal',
            fontWeight: 500,
            fontSize: '12px',
            lineHeight: '16px',
            color: '#18181b',
            textTransform: 'none',
            letterSpacing: 'normal',
            minWidth: 'auto',
            boxShadow: 'none',
            '&:hover': {
              backgroundColor: '#fafafa',
              borderColor: '#e4e4e7',
              boxShadow: 'none',
            },
            '&:disabled': {
              backgroundColor: '#ffffff',
              color: '#71717a',
              borderColor: '#e4e4e7',
            },
          }}
        >
          <ListFilter size={16} color="#18181b" style={{ flexShrink: 0 }} />
          <span>Filter</span>
        </Button>
      </Box>

      {/* Filter Popover Overlay */}
      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
        sx={{
          '& .MuiPopover-paper': {
            mt: 1,
            minWidth: 600,
            maxWidth: 800,
            bgcolor: '#ffffff',
            border: '1px solid #e4e4e7',
            borderRadius: '8px',
            boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)'
          }
        }}
      >
        <Box>
          {/* Popover Header */}
          <Box sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            p: 3,
            borderBottom: '1px solid #e4e4e7'
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <FilterIcon sx={{ color: '#18181b', fontSize: '20px' }} />
              <Typography
                variant="h6"
                sx={{
                  color: '#18181b',
                  fontFamily: "'Inter', sans-serif",
                  fontWeight: 600,
                  fontSize: '18px',
                  lineHeight: '24px'
                }}
              >
                Data Filters
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              {/* Undo/Redo Buttons */}
              {onUndo && (
                <Tooltip title="Undo (Ctrl+Z)">
                  <span>
                    <Button
                      size="small"
                      onClick={onUndo}
                      disabled={!canUndo || disabled}
                      startIcon={<UndoIcon fontSize="small" />}
                      sx={{
                        color: '#71717a',
                        textTransform: 'none',
                        fontSize: '13px',
                        fontFamily: "'Inter', sans-serif",
                        fontWeight: 500,
                        '&:hover': {
                          color: '#18181b',
                          backgroundColor: '#f4f4f5'
                        },
                        '&:disabled': {
                          color: '#d4d4d8'
                        }
                      }}
                    >
                      Undo filter
                    </Button>
                  </span>
                </Tooltip>
              )}
              {onRedo && (
                <Tooltip title="Redo (Ctrl+Shift+Z)">
                  <span>
                    <Button
                      size="small"
                      onClick={onRedo}
                      disabled={!canRedo || disabled}
                      startIcon={<RedoIcon fontSize="small" />}
                      sx={{
                        color: '#71717a',
                        textTransform: 'none',
                        fontSize: '13px',
                        fontFamily: "'Inter', sans-serif",
                        fontWeight: 500,
                        '&:hover': {
                          color: '#18181b',
                          backgroundColor: '#f4f4f5'
                        },
                        '&:disabled': {
                          color: '#d4d4d8'
                        }
                      }}
                    >
                      Reapply filter
                    </Button>
                  </span>
                </Tooltip>
              )}
              <IconButton
                onClick={handleClose}
                size="small"
                sx={{
                  color: '#71717a',
                  '&:hover': {
                    backgroundColor: '#f4f4f5'
                  }
                }}
              >
                <CloseIcon fontSize="small" />
              </IconButton>
            </Box>
          </Box>

          {/* Filter Content - Compact Rows */}
          <Box sx={{ p: 3, maxHeight: '70vh', overflowY: 'auto', backgroundColor: '#ffffff' }}>
            <Stack spacing={2}>
              {filters.map((filter) => renderCompactFilter(filter))}
            </Stack>
          </Box>

          {/* Footer with Apply/Revert Buttons */}
          {hasChanges && (
            <Box sx={{
              p: 2,
              borderTop: '1px solid #e4e4e7',
              display: 'flex',
              justifyContent: 'flex-end',
              gap: 1.5,
              backgroundColor: '#fafafa'
            }}>
              <Button
                size="small"
                variant="outlined"
                onClick={() => {
                  setLocalFilterValues(filterValues);
                  setHasChanges(false);
                }}
                sx={{
                  textTransform: 'none',
                  fontFamily: "'Inter', sans-serif",
                  color: '#71717a',
                  borderColor: '#e4e4e7',
                  '&:hover': {
                    backgroundColor: '#f4f4f5',
                    borderColor: '#d4d4d8'
                  }
                }}
              >
                Revert Filters
              </Button>
              <Button
                size="small"
                variant="contained"
                onClick={() => {
                  onFilterChange(localFilterValues);
                  setHasChanges(false);
                  handleClose();
                }}
                sx={{
                  textTransform: 'none',
                  fontFamily: "'Inter', sans-serif",
                  backgroundColor: '#f97316',
                  color: '#ffffff',
                  '&:hover': {
                    backgroundColor: '#ea580c'
                  }
                }}
              >
                Apply Filters
              </Button>
            </Box>
          )}
        </Box>
      </Popover>

      {/* Nested Popover for Editing Individual Filter */}
      <Popover
        open={Boolean(filterEditAnchor) && editingFilterId !== null}
        anchorEl={filterEditAnchor}
        onClose={handleFilterEditClose}
        disableEnforceFocus={true}
        disableAutoFocus={true}
        disableRestoreFocus={true}
        anchorOrigin={{
          vertical: 'center',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'center',
          horizontal: 'left',
        }}
        sx={{
          '& .MuiPopover-paper': {
            ml: 1,
            minWidth: 400,
            maxWidth: 'calc(100vw - 120px)',
            maxHeight: '90vh',
            height: 'auto',
            bgcolor: '#ffffff',
            border: '1px solid #e4e4e7',
            borderRadius: '8px',
            boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
            display: 'flex',
            flexDirection: 'column'
          }
        }}
      >
        {editingFilterId && (() => {
          const currentFilter = filters.find(f => f.filterId === editingFilterId);
          const isHierarchical = currentFilter?.hierarchical || false;
          // Use pending value if available, otherwise use initial filter value
          const currentValue = pendingFilterValue !== null
            ? pendingFilterValue
            : (localFilterValues[editingFilterId] || currentFilter?.defaultValue);
          const selectedValues = Array.isArray(currentValue) ? currentValue : [currentValue].filter(Boolean);
          const allOptions = currentFilter?.options || [];

          // For hierarchical, get all leaf nodes
          const getAllLeafNodes = (opts: any[]): any[] => {
            const leafNodes: any[] = [];
            const traverse = (nodes: any[]) => {
              nodes.forEach(node => {
                if (!node.children || node.children.length === 0) {
                  leafNodes.push(node);
                } else {
                  traverse(node.children);
                }
              });
            };
            traverse(opts);
            return leafNodes;
          };

          const leafNodes = isHierarchical ? getAllLeafNodes(allOptions) : allOptions;
          const allLeafValues = leafNodes.map(opt => opt.value);
          const allSelected = isHierarchical
            ? allLeafValues.length > 0 && allLeafValues.every(val => selectedValues.includes(val))
            : allOptions.length > 0 && allOptions.every(opt => selectedValues.includes(opt.value));

          const handleSelectAll = () => {
            if (isHierarchical) {
              setPendingFilterValue(allLeafValues);
            } else {
              const allValues = allOptions.map(opt => opt.value);
              setPendingFilterValue(allValues);
            }
          };

          const handleDeselectAll = () => {
            setPendingFilterValue([]);
          };

          return (
            <Box sx={{
              p: 3,
              display: 'flex',
              flexDirection: 'column',
              height: '100%',
              minHeight: 0
            }}>
              {/* Title and Select All button row - anchored to top */}
              <Box sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                mb: 3,
                flexShrink: 0
              }}>
                <Typography
                  variant="subtitle1"
                  sx={{
                    color: '#18181b',
                    fontFamily: "'Inter', sans-serif",
                    fontWeight: 600,
                    fontSize: '16px',
                    lineHeight: '24px'
                  }}
                >
                  {isHierarchical
                    ? `${currentFilter?.label} – ${selectedValues.length} selected`
                    : currentFilter?.label}
                </Typography>
                {isHierarchical && currentFilter?.allowSelectAll !== false && (
                  <Button
                    size="small"
                    variant={allSelected ? "contained" : "outlined"}
                    onClick={allSelected ? handleDeselectAll : handleSelectAll}
                    sx={{
                      fontFamily: "'Inter', sans-serif",
                      fontWeight: 500,
                      fontSize: '14px',
                      lineHeight: '20px',
                      textTransform: 'none',
                      color: allSelected ? '#ffffff' : '#18181b',
                      backgroundColor: allSelected ? '#f97316' : 'transparent',
                      borderColor: allSelected ? '#f97316' : '#e4e4e7',
                      borderRadius: '6px',
                      padding: '6px 12px',
                      '&:hover': {
                        borderColor: '#f97316',
                        backgroundColor: allSelected ? '#ea580c' : '#fff7ed'
                      }
                    }}
                  >
                    {allSelected ? 'Deselect All' : 'Select All'}
                  </Button>
                )}
              </Box>

              {/* Scrollable filter content */}
              <Box sx={{
                flex: 1,
                minHeight: 0,
                overflow: 'hidden',
                display: 'flex',
                flexDirection: 'column'
              }}>
                {renderFilter(currentFilter!)}
              </Box>

              {/* Save/Cancel buttons and microcopy - anchored to bottom */}
              <Box sx={{
                mt: 3,
                flexShrink: 0,
                pt: 3,
                borderTop: '1px solid #e4e4e7',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                gap: 2
              }}>
                <Typography
                  variant="caption"
                  sx={{
                    fontFamily: "'Inter', sans-serif",
                    fontSize: '12px',
                    lineHeight: '16px',
                    color: '#71717a',
                    flex: '1 1 auto',
                    minWidth: 0
                  }}
                >
                  Changes will apply only when you click the orange &quot;Apply Filters&quot; button.
                </Typography>
                <Box sx={{ display: 'flex', gap: 1.5, flexShrink: 0 }}>
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={handleFilterEditClose}
                    sx={{
                      fontFamily: "'Inter', sans-serif",
                      fontWeight: 500,
                      fontSize: '14px',
                      lineHeight: '20px',
                      color: '#71717a',
                      borderColor: '#e4e4e7',
                      borderRadius: '6px',
                      textTransform: 'none',
                      '&:hover': {
                        backgroundColor: '#f4f4f5',
                        borderColor: '#d4d4d8'
                      }
                    }}
                  >
                    Cancel
                  </Button>
                  <Button
                    size="small"
                    onClick={handleDoneClick}
                    variant="contained"
                    disabled={!isDateRangeValid}
                    startIcon={<CheckIcon />}
                    sx={{
                      fontFamily: "'Inter', sans-serif",
                      fontWeight: 500,
                      fontSize: '14px',
                      lineHeight: '20px',
                      backgroundColor: '#18181b',
                      color: '#ffffff',
                      borderRadius: '6px',
                      textTransform: 'none',
                      '&:hover': {
                        backgroundColor: '#27272a'
                      },
                      '&:disabled': {
                        backgroundColor: '#e4e4e7',
                        color: '#71717a'
                      }
                    }}
                  >
                    Save
                  </Button>
                </Box>
              </Box>
            </Box>
          );
        })()}
      </Popover>
    </>
  );
};

